/**
 * ForeignKeyBasicFields — local cols / referenced table / referenced cols /
 * constraint name inputs. Split out of `ForeignKeyAddFormBody` so the form-
 * body file stays under the 200-line budget. Mirrors the orchestrator-owns-
 * state / presentational-children-accept-setters pattern from Slice A4
 * (`ColumnAddBasicFields`).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B2.
 */
import type { Dispatch, SetStateAction } from 'react';

import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';

export interface ForeignKeyBasicFieldsProps {
  readonly testId: string;
  readonly columnsInput: string;
  readonly setColumnsInput: Dispatch<SetStateAction<string>>;
  readonly refTable: string;
  readonly setRefTable: Dispatch<SetStateAction<string>>;
  readonly refColumnsInput: string;
  readonly setRefColumnsInput: Dispatch<SetStateAction<string>>;
  readonly name: string;
  readonly setName: Dispatch<SetStateAction<string>>;
}

export function ForeignKeyBasicFields(props: ForeignKeyBasicFieldsProps) {
  const {
    testId, columnsInput, setColumnsInput, refTable, setRefTable,
    refColumnsInput, setRefColumnsInput, name, setName,
  } = props;
  return (
    <>
      <Field
        testId={`${testId}-columns`}
        label="Local columns"
        required
        value={columnsInput}
        onChange={setColumnsInput}
        placeholder="customer_id"
        helper="Comma-separated column list."
      />
      <Field
        testId={`${testId}-ref-table`}
        label="Referenced table"
        required
        value={refTable}
        onChange={setRefTable}
        placeholder="customer"
      />
      <Field
        testId={`${testId}-ref-columns`}
        label="Referenced columns"
        required
        value={refColumnsInput}
        onChange={setRefColumnsInput}
        placeholder="id"
        helper="Must match local column count."
      />
      <Field
        testId={`${testId}-name`}
        label="Constraint name (optional)"
        value={name}
        onChange={setName}
        placeholder="fk_table_ref"
      />
    </>
  );
}

interface FieldProps {
  readonly testId: string;
  readonly label: string;
  readonly required?: boolean;
  readonly value: string;
  readonly onChange: Dispatch<SetStateAction<string>>;
  readonly placeholder: string;
  readonly helper?: string;
}
function Field({ testId, label, required, value, onChange, placeholder, helper }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={testId} className="text-xs">
        {label} {required ? <span className="text-destructive">*</span> : null}
      </Label>
      <Input
        id={testId}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 font-mono text-xs"
      />
      {helper ? <span className="text-[10px] text-muted-foreground">{helper}</span> : null}
    </div>
  );
}
